import { useState, type KeyboardEvent } from 'react';
import { slugify } from '@/entities/section-picker';

export interface TemplateNameShellProps {
  mode: 'create' | 'rename';
  /** Seed value for rename; ignored on create. */
  initialName?: string;
  /**
   * Slugs already in use, so a collision is caught here rather than by the server. Only consulted on
   * create — a rename keeps the existing slug, so two templates may share a display name.
   */
  existingSlugs?: string[];
  onCancel: () => void;
  onSubmit: (name: string) => void;
}

/**
 * The name field's state, validation and submit path, held apart from `TemplateNameSheet`
 * so the three call sites share one copy of the rules — the same reason
 * `useBriefTemplateIntakeShell` and `useConfigureContentElementForm` exist.
 *
 * Callers must mount the sheet only while it should be open — the field seeds once, so
 * unmounting is what resets it.
 */
export function useTemplateNameForm({
  mode,
  initialName = '',
  existingSlugs = [],
  onSubmit,
}: Omit<TemplateNameShellProps, 'onCancel'>) {
  const [name, setName] = useState(initialName);

  const trimmed = name.trim();
  // Only creation derives a slug from the name; a rename keeps the existing one, so a name that
  // slugifies to nothing is fine there. On create it is not: the slug is the route param, so an
  // empty one produces a row that can never be opened.
  const slugIsEmpty = mode === 'create' && trimmed.length > 0 && slugify(trimmed).length === 0;
  // Caught here rather than left to the server: the caller closes the shell before the mutation
  // runs, so a rejection landed as a toast with the shell already gone and the typed name lost.
  const slugTaken =
    mode === 'create' && trimmed.length > 0 && existingSlugs.includes(slugify(trimmed));
  const canSubmit =
    trimmed.length > 0 && !slugIsEmpty && !slugTaken && trimmed !== initialName.trim();

  const handleSubmit = () => {
    if (!canSubmit) return;
    onSubmit(trimmed);
  };

  return {
    name,
    setName,
    slugIsEmpty,
    slugTaken,
    canSubmit,
    handleSubmit,
    handleKeyDown: (event: KeyboardEvent<HTMLInputElement>) => {
      if (event.key === 'Enter') handleSubmit();
    },
  };
}
